import { useEffect } from "react";
import { calcAge } from "@/lib/dates";
import {
  X, Activity, AlertTriangle, Ban, ShieldCheck, ClipboardCheck,
  AlertCircle, Phone, Heart, User, Mail, Hash, Ruler, Weight, IdCard, ChevronRight, Cake,
} from "lucide-react";

const CAT_LABELS = {
  tackle: "Tackle",
  flag_masculino: "Flag Masculino",
  flag_femenino: "Flag Femenino",
};

const STATUS_META = {
  active: { label: "Activo", icon: Activity, color: "#34C759" },
  injured: { label: "Lesionado", icon: AlertTriangle, color: "#FF9F0A" },
  suspended: { label: "Suspendido", icon: Ban, color: "#FF3B30" },
};

// Lista de campos que validamos para "Datos pendientes". Cada item define
// si está presente y el label a mostrar.
function buildPendingList(p) {
  const prof = p.profile || {};
  const items = [
    { key: "rut",                label: "RUT",                  present: !!prof.rut },
    { key: "phone",              label: "Teléfono",             present: !!prof.phone },
    { key: "emergency_contact",  label: "Contacto de emergencia", present: !!prof.emergency_contact },
    { key: "emergency_phone",    label: "Teléfono de emergencia", present: !!prof.emergency_phone },
    { key: "categories",         label: "Categoría / rama",       present: (prof.categories || []).length > 0 },
    { key: "position",           label: "Posición",               present: !!prof.position },
    { key: "jersey_number",      label: "Número de camiseta",     present: prof.jersey_number !== null && prof.jersey_number !== undefined },
    { key: "photo",              label: "Foto de perfil",         present: !!(prof.photo_drive_id || prof.photo_path || p.picture) },
  ];
  const missing = items.filter(i => !i.present);
  return { missing, total: items.length, completed: items.length - missing.length };
}

export default function PlayerProfileModal({ player, photoUrl, onClose }) {
  // Bloquea scroll del body + cierra con Escape
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!player) return null;

  const prof = player.profile || {};
  const status = STATUS_META[prof.status || "active"];
  const StatusIcon = status.icon;
  const { missing, total, completed } = buildPendingList(player);
  const completePct = Math.round((completed / total) * 100);
  const complete = missing.length === 0;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-3 md:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="player-profile-modal"
    >
      <div
        className="tactical-card w-full max-w-2xl my-2 md:my-0 bg-[#0A0A0A] flex flex-col max-h-[95vh] md:max-h-[88vh]"
        style={{ borderColor: "#27272A" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 p-4 border-b border-[#27272A] flex-shrink-0">
          <div className="w-14 h-14 md:w-16 md:h-16 bg-[#0A0A0A] border border-[#27272A] overflow-hidden flex-shrink-0">
            {photoUrl ? (
              <img src={photoUrl} alt="" className="w-full h-full object-cover" data-testid="ppm-photo"/>
            ) : player.picture ? (
              <img src={player.picture} alt="" className="w-full h-full object-cover" data-testid="ppm-photo-google"/>
            ) : (
              <div className="w-full h-full flex items-center justify-center font-display text-2xl text-[#27272A]">
                {player.name?.[0] || "?"}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="overline text-[#71717A] flex items-center gap-1 text-[10px]">
              <User size={10}/> Perfil del jugador
            </div>
            <h2 className="font-display text-xl md:text-2xl text-white leading-tight truncate mt-0.5" data-testid="ppm-name">
              {player.name?.toUpperCase() || "—"}
            </h2>
            <div className="flex items-center gap-1.5 text-[11px] text-[#A1A1AA] mt-0.5 truncate">
              <Mail size={10}/> <span className="truncate" data-testid="ppm-email">{player.email || "—"}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border"
                style={{ borderColor: status.color, color: status.color }}
                data-testid="ppm-status"
              >
                <StatusIcon size={10}/> {status.label}
              </span>
              {(prof.categories || []).map(c => (
                <span key={c} className="badge badge-coach text-[9px] px-1.5 py-0.5" data-testid={`ppm-cat-${c}`}>
                  {CAT_LABELS[c] || c}
                </span>
              ))}
              {!(prof.categories || []).length && (
                <span className="badge badge-pending text-[9px] px-1.5 py-0.5">Sin categoría</span>
              )}
            </div>
          </div>
          {(() => {
            const age = calcAge(prof.birth_date);
            return age != null ? (
              <div
                className="hidden md:flex flex-col items-center justify-center px-3 py-1.5 border border-[#27272A] bg-[#0F0F0F] flex-shrink-0"
                title={`Edad: ${age} años`}
                data-testid="ppm-age"
              >
                <Cake size={12} className="text-cyan-400 mb-0.5"/>
                <div className="font-display text-2xl text-white leading-none">{age}</div>
                <div className="text-[9px] uppercase tracking-widest text-[#71717A] mt-0.5">años</div>
              </div>
            ) : null;
          })()}
          <button
            onClick={onClose}
            className="text-[#71717A] hover:text-white flex-shrink-0"
            data-testid="ppm-close"
            aria-label="Cerrar"
          >
            <X size={18}/>
          </button>
        </div>

        {/* Body scrolleable: evita que el modal exceda el viewport */}
        <div className="overflow-y-auto flex-1">

        {/* Atributos tácticos */}
        <SectionTitle icon={ClipboardCheck}>Datos tácticos</SectionTitle>
        <div className="grid grid-cols-4 gap-px bg-[#27272A] border-b border-[#27272A]">
          <Stat icon={Hash}   label="N° camiseta" value={prof.jersey_number ?? "—"} testid="ppm-jersey"/>
          <Stat               label="Posición"    value={prof.position || "—"}      testid="ppm-position">
            {prof.position_secondary && (
              <div className="text-[9px] text-[#71717A] mt-0.5 uppercase tracking-widest">
                2ª: {prof.position_secondary}
              </div>
            )}
          </Stat>
          <Stat icon={Ruler}  label="Altura"      value={prof.height_cm ? `${prof.height_cm} cm` : "—"} testid="ppm-height"/>
          <Stat icon={Weight} label="Peso"        value={prof.weight_kg ? `${prof.weight_kg} kg` : "—"} testid="ppm-weight"/>
        </div>

        {/* Contacto */}
        <SectionTitle icon={Phone}>Contacto</SectionTitle>
        <div className="grid grid-cols-2 gap-px bg-[#27272A] border-b border-[#27272A]">
          <FieldRow icon={IdCard} label="RUT"             value={prof.rut}              testid="ppm-rut"/>
          <FieldRow icon={Phone}  label="Teléfono"        value={prof.phone}            testid="ppm-phone"/>
          <FieldRow
            icon={User}
            label="Contacto emerg."
            value={prof.emergency_contact ? (prof.emergency_relationship ? `${prof.emergency_contact} (${prof.emergency_relationship})` : prof.emergency_contact) : null}
            testid="ppm-emerg-name"
          />
          <FieldRow icon={Phone}  label="Tel. emergencia" value={prof.emergency_phone}   testid="ppm-emerg-phone"/>
        </div>

        {/* Médico (opcional - solo si hay algo) */}
        {(prof.blood_type || prof.health_coverage || prof.health_plan || prof.allergies || prof.medical_notes) && (
          <>
            <SectionTitle icon={Heart}>Información médica</SectionTitle>
            <div className="grid grid-cols-2 gap-px bg-[#27272A] border-b border-[#27272A]">
              <FieldRow label="Grupo sanguíneo" value={prof.blood_type}      testid="ppm-blood"/>
              <FieldRow label="Previsión"       value={prof.health_coverage} testid="ppm-coverage"/>
              <FieldRow label="Plan"            value={prof.health_plan}     testid="ppm-plan"/>
              <FieldRow label="Alergias"        value={prof.allergies}       testid="ppm-allergies"/>
              {prof.medical_notes && (
                <div className="col-span-2 px-3 py-2 bg-[#0A0A0A]">
                  <div className="overline text-[#71717A] mb-1 text-[10px]">Notas médicas</div>
                  <div className="text-[13px] text-white whitespace-pre-wrap leading-snug" data-testid="ppm-medical-notes">
                    {prof.medical_notes}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Completitud del perfil */}
        <SectionTitle icon={ShieldCheck}>Estado del perfil</SectionTitle>
        <div className="p-4 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="overline text-[#A1A1AA] text-[10px]">Completitud</span>
              <span className="font-mono text-xs" style={{ color: complete ? "#34C759" : "#FACC15" }} data-testid="ppm-completion-pct">
                {completePct}% ({completed}/{total})
              </span>
            </div>
            <div className="h-1.5 bg-[#0A0A0A] border border-[#27272A] overflow-hidden">
              <div
                className="h-full transition-all"
                style={{ width: `${completePct}%`, background: complete ? "#34C759" : "#FACC15" }}
              />
            </div>
          </div>

          {complete ? (
            <div
              className="flex items-center gap-2 px-3 py-2 border"
              style={{ borderColor: "#34C759", background: "rgba(52,199,89,0.06)" }}
              data-testid="ppm-complete-banner"
            >
              <ShieldCheck size={16} className="text-[#34C759] flex-shrink-0"/>
              <div className="text-xs text-white font-bold">Perfil completo</div>
            </div>
          ) : (
            <div data-testid="ppm-pending-section">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertCircle size={12} className="text-[#FACC15]"/>
                <span className="overline text-[#FACC15] text-[10px]">Datos pendientes ({missing.length})</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                {missing.map(m => (
                  <li
                    key={m.key}
                    className="flex items-center gap-1.5 text-[12px] text-[#A1A1AA]"
                    data-testid={`ppm-pending-${m.key}`}
                  >
                    <ChevronRight size={10} className="text-[#FACC15] flex-shrink-0"/>
                    <span className="truncate">{m.label}</span>
                  </li>
                ))}
              </ul>
              <div className="text-[9px] uppercase tracking-widest text-[#52525B] mt-2">
                Pídele al jugador que complete su ficha desde &quot;Mi Ficha&quot;.
              </div>
            </div>
          )}
        </div>

        </div>{/* /scroll body */}
      </div>
    </div>
  );
}

// ---- helpers UI ----
function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="px-4 py-1.5 bg-[#0A0A0A] border-b border-[#27272A] flex items-center gap-1.5 sticky top-0 z-10">
      {Icon && <Icon size={11} className="text-cyan-400"/>}
      <span className="overline text-cyan-400 text-[10px]">{children}</span>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid, children }) {
  return (
    <div className="px-3 py-2 bg-[#0A0A0A]">
      <div className="overline text-[#71717A] flex items-center gap-1 mb-0.5 text-[9px]">
        {Icon && <Icon size={10}/>} {label}
      </div>
      <div className="font-display text-base md:text-lg text-white leading-tight" data-testid={testid}>{value}</div>
      {children}
    </div>
  );
}

function FieldRow({ icon: Icon, label, value, testid }) {
  const empty = !value;
  return (
    <div className="px-3 py-2 bg-[#0A0A0A]">
      <div className="overline text-[#71717A] flex items-center gap-1 mb-0.5 text-[9px]">
        {Icon && <Icon size={10}/>} {label}
      </div>
      <div
        className={`text-[13px] ${empty ? "text-[#52525B] italic" : "text-white"} leading-tight`}
        data-testid={testid}
      >
        {empty ? "Sin registrar" : value}
      </div>
    </div>
  );
}
